import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isStaffRole } from '../utils/roles';
import { getDashboardPath } from '../constants/siteNav';
import { authAPI } from '../lib/api';
import AuthShell, {
  AuthHeader,
  AuthFooterLink,
  AuthSubmitButton,
  AuthError,
  AuthBackHome
} from '../components/auth/AuthShell';
import AuthField from '../components/auth/AuthField';

function getSafeReturnPath(returnTo, user) {
  if (!returnTo || !returnTo.startsWith('/') || returnTo.startsWith('//')) {
    return null;
  }
  // Never treat the homepage as a post-login destination
  if (returnTo === '/' || returnTo.startsWith('/?') || returnTo.startsWith('/#')) {
    return null;
  }
  // Staff portal routes: organizers + admins; other /admin paths need staff role
  if (returnTo.startsWith('/admin') && !isStaffRole(user?.role)) {
    return null;
  }
  return returnTo;
}

const LoginPage = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const requestedReturnTo = searchParams.get('returnTo');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = await authAPI.login({
        email: formData.email.trim().toLowerCase(),
        password: formData.password
      });

      if (data.success) {
        const user = data.data.user;
        login(user, data.data.token);

        const returnTo = getSafeReturnPath(requestedReturnTo, user);
        navigate(returnTo || getDashboardPath(user), { replace: true });
      } else {
        setError(data.message || 'Login failed');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to connect to server';
      setError(
        /timeout/i.test(msg)
          ? 'Sign-in is taking too long. Check your connection and try again.'
          : msg
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell footer={<AuthBackHome />}>
      <AuthHeader
        title="Welcome back"
        subtitle={
          requestedReturnTo?.startsWith('/admin')
            ? 'Sign in to access the admin dashboard'
            : 'Sign in to your Niko Vibe account'
        }
      />

      <form onSubmit={handleSubmit} className="space-y-5">
        <AuthError message={error} />

        <AuthField
          id="email"
          label="Email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="Enter your email"
          autoComplete="email"
          icon="envelope"
          required
        />

        <div>
          <AuthField
            id="password"
            label="Password"
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Enter your password"
            autoComplete="current-password"
            icon="lock"
            required
          />
          <div className="mt-2">
            <Link to="/forgot-password" className="text-sm text-gray-500 hover:text-gray-800 transition-colors">
              Forgot password?
            </Link>
          </div>
        </div>

        <AuthSubmitButton loading={loading} loadingText="Signing in…">
          Sign in
        </AuthSubmitButton>

        <AuthFooterLink
          prompt="Don't have an account?"
          linkText="Sign up"
          to={
            requestedReturnTo
              ? `/register?returnTo=${encodeURIComponent(requestedReturnTo)}`
              : '/register'
          }
        />
      </form>
    </AuthShell>
  );
};

export default LoginPage;
